import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheese,
  
  faInfoCircle,
  faTimes,
  faBan,
  faFire
} from '@fortawesome/free-solid-svg-icons';
import './CheeseSelector.css';
import useDatabase from '../../hooks/useDatabase'; // Import useDatabase

const CheeseSelector = ({ selectedCheeses, onUpdateCheeses, includedCheese, isCold = false }) => {
  const { getAllCheeses, isLoading: dbIsLoading } = useDatabase(); // Get cheese data
  const [cheeseOptions, setCheeseOptions] = useState([]);

  // Load cheeses from database
  useEffect(() => {
    if (!dbIsLoading) {
      const fetchedCheeses = getAllCheeses();
      // Add "No Cheese" option manually at the beginning
      const optionsWithNoCheese = [
        { id: 0, name: 'No Cheese', icon: faBan, tag: includedCheese ? 'Save $1.50' : 'Cheese-Free' },
        ...fetchedCheeses.map(cheese => ({ ...cheese, icon: faCheese })) // Add default icon
      ];
      setCheeseOptions(optionsWithNoCheese);
    }
  }, [dbIsLoading, getAllCheeses, includedCheese]);

  // State for cheese melting option
  const [meltCheese, setMeltCheese] = useState(false);
  
  // Reset melt cheese option when temperature changes to cold
  React.useEffect(() => {
    if (isCold && meltCheese) {
      setMeltCheese(false);
      
      // Update any existing cheeses to not be melted
      if (selectedCheeses.length > 0 && selectedCheeses[0].id !== 0) {
        const updatedCheeses = selectedCheeses.map(cheese => ({
          ...cheese,
          melted: false
        }));
        onUpdateCheeses(updatedCheeses);
      }
    }
  }, [isCold, meltCheese, selectedCheeses, onUpdateCheeses]);
  
  // selectedCheeses format: Array of { id, quantity, melted } objects
  // cheeseOptions are now loaded from the useEffect hook above
  
  // Cheese quantity options
  const quantityOptions = [
    { value: 'single', label: 'Regular ($1.50)' },
    { value: 'double', label: 'Double ($3.00)' },
    { value: 'triple', label: 'Triple ($4.50)' }
  ];
  
  // Handle cheese melting option
  const handleMeltCheeseChange = () => {
    const newMeltedState = !meltCheese;
    setMeltCheese(newMeltedState);
    
    // Update all selected cheeses with the new melted state
    if (selectedCheeses.length > 0 && selectedCheeses[0].id !== 'no-cheese') {
      const updatedCheeses = selectedCheeses.map(cheese => ({
        ...cheese,
        melted: newMeltedState
      }));
      onUpdateCheeses(updatedCheeses);
    }
  };
  
  // Automatically set Swiss as the selected cheese if it's included in the style and no other cheese is selected
  React.useEffect(() => {
    // Check if "No Cheese" has been explicitly selected
    const hasNoCheeseSelected = selectedCheeses.some(cheese => cheese.id === 0);
    
    // Don't auto-select included cheese if "No Cheese" is selected
    if (includedCheese && !hasNoCheeseSelected) {
      // Only add the default included cheese if no cheeses are selected
      if (selectedCheeses.length === 0) {
        // Add the included cheese to the selection with quantity single
        // For Special and Super styles, Swiss (id 1) is the included cheese
        const updatedCheeses = [{ id: includedCheese, quantity: 'single', isIncluded: true, melted: meltCheese }];
        onUpdateCheeses(updatedCheeses);
      }
    }
  }, [includedCheese, selectedCheeses, onUpdateCheeses, meltCheese]);

  const handleCheeseClick = (cheeseId) => {
    // Special handling for "No Cheese" option (id 0)
    if (cheeseId === 0) {
      // If "No Cheese" is clicked, clear all cheese selections and set "No Cheese"
      onUpdateCheeses([{ id: 0, quantity: 'single', isIncluded: false, melted: false }]);
      return;
    }
    
    // If a regular cheese is selected, first check if "No Cheese" is selected
    if (selectedCheeses.some(cheese => cheese.id === 0)) {
      // If "No Cheese" is currently selected, replace it with this cheese
      onUpdateCheeses([{ 
        id: cheeseId, 
        quantity: 'single', 
        isIncluded: includedCheese ? true : false, 
        melted: meltCheese 
      }]);
      return;
    }
    
    // Normal cheese selection logic for regular cheeses
    // Start with current selections
    let updatedCheeses = [...selectedCheeses];
    
    // Find if this cheese is already selected
    const existingIndex = updatedCheeses.findIndex(cheese => cheese.id === cheeseId);
    const isSelected = existingIndex !== -1;
    
    // If we already have 3 cheeses and trying to add a new one, don't allow it
    if (!isSelected && updatedCheeses.length >= 3) {
      alert("You can select up to 3 cheeses at a time.");
      return;
    }
    
    // If the cheese is already selected, deselect it
    if (isSelected) {
      // Check if removing an included cheese
      const isRemovingIncludedCheese = updatedCheeses[existingIndex].isIncluded;
      
      // Allow completely removing cheese (instead of replacing it)
      updatedCheeses.splice(existingIndex, 1);
      
      // If we're removing the included cheese and there are other cheeses,
      // mark the first remaining cheese as included
      if (isRemovingIncludedCheese && updatedCheeses.length > 0 && includedCheese) {
        updatedCheeses = updatedCheeses.map((cheese, index) => ({
          ...cheese,
          isIncluded: index === 0 // Mark the first cheese as included
        }));
      }
      
      onUpdateCheeses(updatedCheeses);
      return;
    }
    
    // Otherwise, add the new cheese
    let newCheese = {
      id: cheeseId,
      quantity: 'single',
      isIncluded: false,
      melted: meltCheese // Apply the current melt setting
    };
    
    // Check if this is the first cheese being added and we have an included style
    if (updatedCheeses.length === 0 && includedCheese) {
      // If this is the first cheese with an included style, mark it as included
      newCheese.isIncluded = true;
    }
    
    updatedCheeses.push(newCheese);
    onUpdateCheeses(updatedCheeses);
  };
  
  // Update quantity for a specific cheese
  const handleQuantityChange = (cheeseId, newQuantity) => {
    const updatedCheeses = selectedCheeses.map(cheese => 
      cheese.id === cheeseId ? { ...cheese, quantity: newQuantity } : cheese
    );
    onUpdateCheeses(updatedCheeses);
  };
  
  // Check if a cheese is marked as the included one
  const isIncludedCheese = (cheeseId) => {
    if (!includedCheese) return false;
    
    const cheese = selectedCheeses.find(c => c.id === cheeseId);
    return cheese && cheese.isIncluded;
  };
  
  // Helper function to check if a cheese is selected
  const isCheeseSelected = (cheeseId) => {
    return selectedCheeses.some(cheese => cheese.id === cheeseId);
  };
  
  
  
  return (
    <div className="cheese-selector-container">
      {/* Cheese Grid */}
      <div className="cheese-grid">
        {cheeseOptions.map(cheese => (
          <div key={cheese.id} className="cheese-item-wrapper">
            {cheese.tag && <div className="cheese-badge">{cheese.tag}</div>}
            {isIncludedCheese(cheese.id) && <div className="included-badge">Included</div>}
            <div
              className={`cheese-card ${isCheeseSelected(cheese.id) ? 'selected' : ''} ${isIncludedCheese(cheese.id) ? 'included-cheese' : ''}`}
              onClick={() => handleCheeseClick(cheese.id)}
            >
              <FontAwesomeIcon icon={cheese.icon} className="cheese-icon" />
              <span className="cheese-name">{cheese.name}</span>
            </div>
          </div>
        ))}
      </div>
      
      {/* Cheese Melting Option */}
      {selectedCheeses.length > 0 && selectedCheeses[0].id !== 0 && (
        <div className={`cheese-melting-option ${isCold ? 'disabled' : ''}`}>
          <label className={`melt-option-container ${isCold ? 'disabled' : ''}`}>
            <input
              type="checkbox"
              checked={meltCheese}
              onChange={handleMeltCheeseChange}
              disabled={isCold}
            />
            <span className="melt-option-label">
              <FontAwesomeIcon icon={faFire} className="melt-icon" />
              Melt the cheese
              {isCold && <span className="disabled-text"> (only available with hot sandwiches)</span>}
            </span>
          </label>
        </div>
      )}
      
      {/* Multi-Cheese Selection Summary */}
      {selectedCheeses.length > 0 && (
        <div className="cheese-selection-summary">
          <div className="summary-header">
            <h4 className="summary-title">Selected Cheeses ({selectedCheeses.length}/3)</h4>
          </div>
          
          <div className="selected-cheeses-list">
            {selectedCheeses.map(selectedCheese => {
              const cheeseDetails = cheeseOptions.find(c => c.id === selectedCheese.id);
              
              
              return (
                <div key={selectedCheese.id} className="selected-cheese-item">
                  <div className={`selected-cheese-pill ${selectedCheese.isIncluded ? 'included-pill' : ''}`}>
                    <FontAwesomeIcon icon={selectedCheese.id === 0 ? faBan : faCheese} className="selected-cheese-icon" />
                    <span className="selected-cheese-name">{cheeseDetails?.name}</span>
                    {selectedCheese.isIncluded && selectedCheese.quantity === 'single' && <span className="included-note">(Included)</span>}
                    {selectedCheese.melted && selectedCheese.id !== 0 && (
                      <span className="melted-indicator">
                        <FontAwesomeIcon icon={faFire} /> Melted
                      </span>
                    )}
                  </div>
                  
                  {/* Quantity selector - don't show for No Cheese */}
                  {selectedCheese.id !== 0 && (
                    <div className="cheese-quantity-selector">
                      {selectedCheese.isIncluded ? (
                        /* For included cheese, show different options */
                        <>
                          <label className="quantity-radio">
                            <input
                              type="radio"
                              name={`quantity-${selectedCheese.id}`}
                              value="single"
                              checked={selectedCheese.quantity === 'single'}
                              onChange={() => handleQuantityChange(selectedCheese.id, 'single')}
                            />
                            <span className="quantity-label">Regular (Included)</span>
                          </label>
                          <label className="quantity-radio">
                            <input
                              type="radio"
                              name={`quantity-${selectedCheese.id}`}
                              value="double"
                              checked={selectedCheese.quantity === 'double'}
                              onChange={() => handleQuantityChange(selectedCheese.id, 'double')}
                            />
                            <span className="quantity-label">Double (+$1.50)</span>
                          </label>
                          <label className="quantity-radio">
                            <input
                              type="radio"
                              name={`quantity-${selectedCheese.id}`}
                              value="triple"
                              checked={selectedCheese.quantity === 'triple'}
                              onChange={() => handleQuantityChange(selectedCheese.id, 'triple')}
                            />
                            <span className="quantity-label">Triple (+$3.00)</span>
                          </label>
                        </>
                      ) : (
                        /* For non-included cheese */
                        quantityOptions.map(option => (
                          <label key={option.value} className="quantity-radio">
                            <input
                              type="radio"
                              name={`quantity-${selectedCheese.id}`}
                              value={option.value}
                              checked={selectedCheese.quantity === option.value}
                              onChange={() => handleQuantityChange(selectedCheese.id, option.value)}
                            />
                            <span className="quantity-label">{option.label}</span>
                          </label>
                        ))
                      )}
                    </div>
                  )}
                  
                  <button 
                    className="remove-cheese-btn" 
                    onClick={() => handleCheeseClick(selectedCheese.id)}
                    aria-label="Remove cheese"
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Info about cheese */}
      <div className="cheese-info-note">
        <h4>
          <FontAwesomeIcon icon={faInfoCircle} />
          About Our Cheeses
        </h4>
        <p>
          {includedCheese ? 
            'One cheese is included free with Special and Super styles. You can select any cheese as your included option and up to 3 different cheeses total. Swiss comes standard but can be exchanged. Select "No Cheese" to save $1.50 if you prefer no cheese at all.' :
            'Add up to 3 different cheeses for an additional $1.50 each. Double or triple portions are available for an extra charge.'
          }
        </p>
      </div>
    </div>
  );
};

export default CheeseSelector;